import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Text, VStack, Spinner, useColorModeValue, Flex, Button,
  useToast, HStack, Icon, Tag, Badge, Progress, IconButton, Tooltip,
  Alert, AlertIcon, AlertDescription, Stat, StatLabel, StatNumber,
  StatHelpText, SimpleGrid, Collapse, useDisclosure, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Input, Select, FormControl, FormLabel, Switch, Menu, 
  MenuButton, MenuList, MenuItem, Code, Skeleton
} from '@chakra-ui/react';
import {
  FaLightbulb, FaChartLine, FaCheckCircle, FaClock, FaFilter, 
  FaDownload
} from 'react-icons/fa';
import { 
  ChevronDownIcon, ChevronUpIcon, SettingsIcon, RepeatIcon
} from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';

// --- ENTERPRISE API INTEGRATION ---
import { anomalyApiService } from '../../services/anomalyApiService';
import Card from '../card/Cards'; // FlowSense Enterprise Card Wrapper

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const FADE_UP = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.26, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const STAGGER_CONTAINER = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const LIST_ITEM_VARIANT = {
  hidden:  { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0,   transition: { duration: 0.22, ease: 'easeOut' } },
  exit:    { opacity: 0, x: 12,  transition: { duration: 0.14 } },
};

const MotionBox = motion(Box);

// ============================================================================
// TELEMETRY AGGREGATION ENGINE (Raw Data -> AI Suggestions)
// ============================================================================
/**
 * Takes raw telemetry logs from the backend and groups them by sensor_id
 * to generate actionable rule suggestions.
 */
const groupAnomaliesIntoRules = (rawAnomalies) => {
  const grouped = {};
  
  rawAnomalies.forEach(anomaly => {
    if (!grouped[anomaly.sensor_id]) {
      grouped[anomaly.sensor_id] = {
        sensor_id: anomaly.sensor_id,
        anomaly_count: 0,
        confidence_sum: 0,
        values: [],
        metadata: typeof anomaly.metadata === 'string' ? JSON.parse(anomaly.metadata) : (anomaly.metadata || {})
      };
    }
    grouped[anomaly.sensor_id].anomaly_count += 1;
    grouped[anomaly.sensor_id].confidence_sum += parseFloat(anomaly.confidence_score || 0);
    grouped[anomaly.sensor_id].values.push(parseFloat(anomaly.value || 0));
  });

  return Object.values(grouped).map(group => {
    const max_value = Math.max(...group.values);
    const min_value = Math.min(...group.values);
    const avg_value = group.values.reduce((a, b) => a + b, 0) / group.values.length;
    
    // Determine if the anomaly trend is spiking (high) or dropping (low) based on historical mean
    const historicalMean = group.metadata.historical_mean || 0;
    const anomaly_type = avg_value > historicalMean ? 'high' : 'low';

    return {
      sensor_id: group.sensor_id,
      anomaly_count: group.anomaly_count,
      confidence: group.confidence_sum / group.anomaly_count, // Average confidence
      min_value,
      max_value,
      avg_value,
      anomaly_type,
      unit: group.metadata.unit || 'units'
    };
  });
};

// ============================================================================
// SKELETON LOADER
// ============================================================================
const RuleWidgetSkeleton = () => {
  const bd = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  return (
    <VStack spacing={4} align="stretch" p={2}>
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={2}>
         <Skeleton height="70px" borderRadius="md" />
         <Skeleton height="70px" borderRadius="md" />
         <Skeleton height="70px" borderRadius="md" />
      </SimpleGrid>
      <Skeleton height="20px" w="150px" mb={2} />
      <VStack spacing={3}>
        {[1, 2, 3].map(i => (
          <Box key={i} p={4} borderWidth="1px" borderColor={bd} borderRadius="md" w="full">
             <Flex justify="space-between">
                <VStack align="start" spacing={3}>
                   <HStack>
                     <Skeleton height="20px" w="60px" />
                     <Skeleton height="20px" w="80px" />
                   </HStack>
                   <Skeleton height="12px" w="200px" />
                   <Skeleton height="10px" w="140px" />
                </VStack>
                <Skeleton height="24px" w="60px" borderRadius="md" />
             </Flex>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

// ============================================================================
// RULE CONFIGURATION MODAL
// ============================================================================
const RuleConfigModal = ({ isOpen, onClose, rule, onConfirm, isCreating }) => {
  const [config, setConfig] = useState({
    name: '',
    severity_level: 3,
    threshold_value: 0,
    cooldown_seconds: 600,
    is_enabled: true,
  });
  
  // Hooks must be at the top level
  const summaryBg = useColorModeValue("gray.50", "gray.700");
  const modalFooterBorderColor = useColorModeValue('gray.100', 'gray.700');
  const summaryBorderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (rule) {
      // Map aggregated stats to the Enterprise Backend Rule Schema
      const severity = rule.anomaly_count > 50 ? 5 : rule.anomaly_count > 20 ? 4 : 3;
      const threshold = rule.anomaly_type === 'low' ? Math.floor(rule.min_value || 10) : Math.ceil(rule.max_value || 90);
      
      setConfig({
        name: `AI Auto-Rule: ${rule.sensor_id} Bounds`,
        severity_level: severity,
        threshold_value: threshold,
        cooldown_seconds: 600, // 10 minutes
        is_enabled: true,
      });
    }
  }, [rule]);

  const handleSubmit = () => {
    onConfirm({ ...rule, ...config });
  };

  const severityMapping = {
    1: { label: 'Info', color: 'blue' },
    2: { label: 'Low', color: 'cyan' },
    3: { label: 'Medium', color: 'yellow' },
    4: { label: 'High', color: 'orange' },
    5: { label: 'Critical', color: 'red' }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(4px)" bg="rgba(0,0,0,0.5)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader fontFamily="'Syne', sans-serif" fontWeight="800">Configure AI Alert Rule</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Rule Name</FormLabel>
              <Input
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="e.g., Critical Pressure Spike"
                fontFamily="'IBM Plex Mono', monospace"
                fontSize="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Severity Level</FormLabel>
              <Select
                value={config.severity_level}
                onChange={(e) => setConfig({ ...config, severity_level: parseInt(e.target.value) })}
                fontFamily="'IBM Plex Mono', monospace"
                fontSize="sm"
              >
                {Object.entries(severityMapping).map(([level, data]) => (
                  <option key={level} value={level}>{level} - {data.label}</option>
                ))}
              </Select>
              <HStack mt={2} spacing={2}>
                {Object.entries(severityMapping).map(([level, data]) => (
                  <Badge
                    key={level}
                    colorScheme={data.color}
                    variant={config.severity_level === parseInt(level) ? 'solid' : 'outline'}
                    cursor="pointer"
                    onClick={() => setConfig({ ...config, severity_level: parseInt(level) })}
                    fontFamily="'IBM Plex Mono', monospace"
                    fontSize="xs"
                  >
                    {data.label}
                  </Badge>
                ))}
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Threshold Limit</FormLabel>
              <Input
                type="number"
                value={config.threshold_value}
                onChange={(e) => setConfig({ ...config, threshold_value: parseFloat(e.target.value) })}
                fontFamily="'IBM Plex Mono', monospace"
                fontSize="sm"
              />
              <Text fontSize="xs" color="gray.500" mt={1} fontFamily="'IBM Plex Mono', monospace">
                Triggers when sensor reads {rule?.anomaly_type === 'low' ? '<' : '>'} this value.
              </Text>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0" fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Activate Immediately</FormLabel>
              <Switch
                isChecked={config.is_enabled}
                onChange={(e) => setConfig({ ...config, is_enabled: e.target.checked })}
                colorScheme="green"
              />
            </FormControl>

            {rule && (
              <Box p={3} bg={summaryBg} borderRadius="md" borderWidth="1px" borderColor={summaryBorderColor}>
                <Text fontSize="10px" fontWeight="800" mb={2} fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="blue.500">AI TELEMETRY SUMMARY</Text>
                <VStack align="stretch" spacing={1} fontSize="xs" fontFamily="'IBM Plex Mono', monospace">
                  <Text>• Grouped {rule.anomaly_count} raw anomaly events</Text>
                  <Text>• Target Sensor: <Code fontSize="xs" colorScheme="blue">{rule.sensor_id}</Code></Text>
                  <Text>• Deviation Vector: {rule.anomaly_type === 'low' ? 'Negative Drop' : 'Positive Spike'}</Text>
                  <Text>• ML Confidence: {rule.confidence ? `${(rule.confidence * 100).toFixed(0)}%` : 'High'}</Text>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor={modalFooterBorderColor} mt={4}>
          <Button variant="ghost" mr={3} onClick={onClose} fontFamily="'IBM Plex Mono', monospace" fontSize="10px" letterSpacing="0.06em">
            CANCEL
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={isCreating}
            loadingText="DEPLOYING..."
            fontFamily="'IBM Plex Mono', monospace" 
            fontSize="10px" 
            letterSpacing="0.06em"
          >
            DEPLOY RULE
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ============================================================================
// INDIVIDUAL RULE CARD COMPONENT
// ============================================================================
const SuggestedRuleCard = ({ rule, onApprove, onDismiss, onEdit }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'white');
  
  const confidenceColor = rule.confidence > 0.8 ? 'green' : rule.confidence > 0.6 ? 'yellow' : 'orange';
  const severityColor = rule.anomaly_count > 50 ? 'red' : rule.anomaly_count > 20 ? 'orange' : 'yellow';

  return (
    <Box
      p={4}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      _hover={{ shadow: 'md', borderColor: 'blue.400', transform: 'translateY(-1px)' }}
      transition="all 0.2s"
    >
      <Flex justify="space-between" align="flex-start" mb={3}>
        <VStack align="start" spacing={2} flex="1">
          <HStack>
            <Tag colorScheme="blue" size="sm" fontFamily="'IBM Plex Mono', monospace" fontSize="10px">
              {rule.sensor_id}
            </Tag>
            <Badge colorScheme={confidenceColor} fontFamily="'IBM Plex Mono', monospace" fontSize="9px">
              {rule.confidence ? `${(rule.confidence * 100).toFixed(0)}%` : 'High'} Confidence
            </Badge>
          </HStack>
          
          <Text fontSize="sm" color={textColor} fontWeight="medium">
            Aggregated <strong>{rule.anomaly_count}</strong> irregular readings
            <strong> {rule.anomaly_type === 'low' ? ' dropping below' : ' spiking above'}</strong> normal baselines
          </Text>
          
          <HStack fontSize="10px" color="gray.500" spacing={4} fontFamily="'IBM Plex Mono', monospace">
            <HStack>
              <Icon as={FaClock} />
              <Text>LAST 24 HOURS</Text>
            </HStack>
            <HStack>
              <Icon as={FaChartLine} />
              <Text>
                RANGE: {rule.min_value?.toFixed(1) || 'N/A'} - {rule.max_value?.toFixed(1) || 'N/A'}
              </Text>
            </HStack>
          </HStack>
        </VStack>

        <Badge colorScheme={severityColor} fontSize="10px" px={2} py={1} fontFamily="'IBM Plex Mono', monospace" borderRadius="md">
          {rule.anomaly_count > 50 ? 'CRITICAL' : rule.anomaly_count > 20 ? 'HIGH' : 'MEDIUM'}
        </Badge>
      </Flex>

      <Divider my={3} borderColor={borderColor} />

      <HStack spacing={2} justify="flex-end">
        <Tooltip label="Customize thresholds" hasArrow>
          <IconButton
            icon={<SettingsIcon />}
            size="sm"
            variant="ghost"
            onClick={() => onEdit(rule)}
            aria-label="Edit rule"
            borderRadius="lg"
          />
        </Tooltip>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={() => onDismiss(rule)}
          fontFamily="'IBM Plex Mono', monospace" 
          fontSize="10px" 
          letterSpacing="0.06em"
          borderRadius="lg"
        >
          DISMISS
        </Button>
        <Button
          colorScheme="green"
          size="sm"
          leftIcon={<FaCheckCircle />}
          onClick={() => onApprove(rule)}
          fontFamily="'IBM Plex Mono', monospace" 
          fontSize="10px" 
          letterSpacing="0.06em"
          borderRadius="lg"
          boxShadow="sm"
        >
          QUICK APPROVE
        </Button>
      </HStack>
    </Box>
  );
};

// ============================================================================
// MAIN WIDGET COMPONENT
// ============================================================================
const AISuggestedRulesWidget = () => {
  const [suggestedRules, setSuggestedRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [sortBy, setSortBy] = useState('confidence');
  const [dismissedRules, setDismissedRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [stats, setStats] = useState({
    totalSuggestions: 0,
    highPriority: 0,
    avgConfidence: 0
  });
  
  const toast = useToast();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isStatsOpen, onToggle: onToggleStats } = useDisclosure({ defaultIsOpen: true });

  // FlowSense Theme Hooks
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const statBg = useColorModeValue('blue.50', 'blue.900');
  
  const panelHeaderBg = useColorModeValue(
    'linear-gradient(135deg,rgba(236,201,75,0.06),rgba(237,137,54,0.06))',
    'linear-gradient(135deg,rgba(236,201,75,0.08),rgba(237,137,54,0.08))'
  );
  const headerIconBg = useColorModeValue('yellow.50', 'rgba(236, 201, 75, 0.1)');
  const headerIconBorder = useColorModeValue('yellow.200', 'rgba(236, 201, 75, 0.2)');
  const titleGradient = useColorModeValue('linear(to-r, yellow.600, orange.500)', 'linear(to-r, #f6e05e, #ed8936)');
  const iconButtonHoverBg = useColorModeValue('yellow.50', 'rgba(236, 201, 75, 0.1)');

  const scrollbarThumb = useColorModeValue('gray.300', 'gray.600');
  const scrollbarThumbHover = useColorModeValue('gray.400', 'gray.500');
  
  const scrollbarStyles = {
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: '4px' },
    '&::-webkit-scrollbar-thumb:hover': { background: scrollbarThumbHover }
  };

  /**
   * Fetches raw anomalies and system stats, then runs the Aggregation Engine
   */
  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    try {
      // Execute both network calls simultaneously for performance
      const [statsResponse, telemetryResponse] = await Promise.all([
        anomalyApiService.getStats(24),
        anomalyApiService.getRecentAnomalies({ limit: 500 }) // Fetch enough to aggregate
      ]);
      
      const rawAnomalies = telemetryResponse.data || [];
      const aggregatedSuggestions = groupAnomaliesIntoRules(rawAnomalies);
      
      setSuggestedRules(aggregatedSuggestions);
      
      // Use real ML backend stats
      setStats({
        totalSuggestions: aggregatedSuggestions.length,
        highPriority: statsResponse.data?.critical_anomalies || 0,
        avgConfidence: statsResponse.data?.average_confidence || 0
      });

    } catch (error) {
      console.error('[AI-SUGGEST] Integration Fetch Error:', error);
      toast({
        title: 'Connection Failed',
        description: 'Unable to retrieve data from the Enterprise Anomaly Engine.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchRules();
    // Auto-refresh polling every 5 minutes to keep dashboard live
    const interval = setInterval(fetchRules, 300000);
    return () => clearInterval(interval);
  }, [fetchRules]);

  /**
   * Instantly converts an AI suggestion into a Database Alert Rule
   */
  const handleQuickApprove = async (rule) => {
    setIsCreating(true);
    try {
      const severityLevel = rule.anomaly_count > 50 ? 5 : rule.anomaly_count > 20 ? 4 : 3;
      const condition = rule.anomaly_type === 'low' ? '<' : '>';
      const threshold = rule.anomaly_type === 'low' ? Math.floor(rule.min_value) : Math.ceil(rule.max_value);

      const newRuleData = {
        name: `AI Auto-Rule: ${rule.sensor_id}`,
        sensor_id: rule.sensor_id,
        logic_json: { condition: condition, threshold: threshold },
        severity_level: severityLevel,
        cooldown_seconds: 600,
      };

      await anomalyApiService.createAiRule(newRuleData);

      toast({
        title: 'Rule Deployed Successfully',
        description: `Backend monitoring enabled for ${rule.sensor_id}.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Remove from UI
      handleDismiss(rule);
    } catch (error) {
      toast({
        title: 'Deployment Failed',
        description: error.response?.data?.error?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    onModalOpen();
  };

  const handleConfirmRule = async (ruleWithConfig) => {
    setIsCreating(true);
    try {
      const condition = ruleWithConfig.anomaly_type === 'low' ? '<' : '>';
      
      const newRuleData = {
        name: ruleWithConfig.name,
        sensor_id: ruleWithConfig.sensor_id,
        logic_json: { condition: condition, threshold: ruleWithConfig.threshold_value },
        severity_level: ruleWithConfig.severity_level,
        cooldown_seconds: ruleWithConfig.cooldown_seconds,
      };

      await anomalyApiService.createAiRule(newRuleData);

      toast({
        title: 'Custom Rule Deployed',
        description: 'Your configured alert rule is now active in the enterprise grid.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      onModalClose();
      handleDismiss(ruleWithConfig);
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error.response?.data?.error?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDismiss = (rule) => {
    setDismissedRules(prev => [...prev, rule.sensor_id + rule.anomaly_type]);
  };

  /**
   * Enterprise Data Streaming to Local CSV
   */
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await anomalyApiService.exportAnomaliesCsv();
      
      // Convert Blob Stream to downloadable file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `flowsense_anomalies_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Export Complete',
        description: 'Telemetry data downloaded successfully.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Unable to stream data from the server.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Filter and sort rules
  const filteredRules = useMemo(() => {
    let rules = suggestedRules.filter(
      rule => !dismissedRules.includes(rule.sensor_id + rule.anomaly_type)
    );

    // Apply severity filter
    if (filterSeverity !== 'all') {
      rules = rules.filter(rule => {
        const severity = rule.anomaly_count > 50 ? 'critical' : 
                         rule.anomaly_count > 20 ? 'high' : 'medium';
        return severity === filterSeverity;
      });
    }

    // Apply sorting
    rules.sort((a, b) => {
      switch (sortBy) {
        case 'confidence':
          return (b.confidence || 0.8) - (a.confidence || 0.8);
        case 'severity':
          return b.anomaly_count - a.anomaly_count;
        case 'sensor':
          return a.sensor_id.localeCompare(b.sensor_id);
        default:
          return 0;
      }
    });

    return rules;
  }, [suggestedRules, dismissedRules, filterSeverity, sortBy]);

  return (
    <Card>
      {/* HEADER SECTION */}
      <Box px={0} pb={4} background={panelHeaderBg} borderRadius="xl" mb={4} p={4}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Box p={2.5} borderRadius="xl" bg={headerIconBg} borderWidth="1px" borderColor={headerIconBorder}>
              <Icon as={FaLightbulb} boxSize={5} color="yellow.500" />
            </Box>
            <VStack spacing={0} align="flex-start">
              <Heading size="md" fontWeight="800" bgGradient={titleGradient} bgClip="text" fontFamily="'Syne', sans-serif">
                AI-Suggested Alert Rules
              </Heading>
              <Text fontSize="9px" color={textColor} letterSpacing="0.1em" fontFamily="'IBM Plex Mono', monospace" textTransform="uppercase">
                MACHINE LEARNING ANOMALY DETECTION
              </Text>
            </VStack>
            {filteredRules.length > 0 && (
              <Badge colorScheme="purple" fontSize="9px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.08em">
                {filteredRules.length} SUGGESTIONS
              </Badge>
            )}
          </HStack>

          <HStack spacing={2} flexWrap="wrap">
            <Tooltip label="Refresh suggestions" hasArrow>
              <IconButton
                icon={<RepeatIcon />}
                size="sm"
                variant="ghost"
                onClick={fetchRules}
                isLoading={isLoading}
                aria-label="Refresh"
                borderRadius="lg"
                _hover={{ bg: iconButtonHoverBg }}
              />
            </Tooltip>
            <Tooltip label="Export telemetry stream to CSV" hasArrow>
              <IconButton
                icon={<FaDownload />}
                size="sm"
                variant="ghost"
                onClick={handleExport}
                isLoading={isExporting}
                isDisabled={suggestedRules.length === 0}
                aria-label="Export"
                borderRadius="lg"
                _hover={{ bg: iconButtonHoverBg }}
              />
            </Tooltip>
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<FaFilter />}
                size="sm"
                variant="ghost"
                aria-label="Filter"
                borderRadius="lg"
                _hover={{ bg: iconButtonHoverBg }}
              />
              <MenuList fontFamily="'IBM Plex Mono', monospace" fontSize="sm">
                <MenuItem onClick={() => setFilterSeverity('all')}>All Severities</MenuItem>
                <MenuItem onClick={() => setFilterSeverity('critical')}>Critical Only</MenuItem>
                <MenuItem onClick={() => setFilterSeverity('high')}>High Priority</MenuItem>
                <MenuItem onClick={() => setFilterSeverity('medium')}>Medium Priority</MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Box>

      {/* STATISTICS SECTION */}
      <Collapse in={isStatsOpen} animateOpacity>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={2}>
          <Stat bg={statBg} p={3} borderRadius="xl" size="sm">
            <StatLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Aggregated Suggestions</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" fontFamily="'IBM Plex Mono', monospace" color="blue.500">{stats.totalSuggestions}</StatNumber>
            <StatHelpText fontSize="9px" fontFamily="'IBM Plex Mono', monospace" m={0}>Based on raw telemetry</StatHelpText>
          </Stat>
          
          <Stat bg={statBg} p={3} borderRadius="xl" size="sm">
            <StatLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Critical Anomalies</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" fontFamily="'IBM Plex Mono', monospace" color="red.400">{stats.highPriority}</StatNumber>
            <StatHelpText fontSize="9px" fontFamily="'IBM Plex Mono', monospace" m={0}>Require immediate action</StatHelpText>
          </Stat>
          
          <Stat bg={statBg} p={3} borderRadius="xl" size="sm">
            <StatLabel fontSize="10px" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">Avg Model Confidence</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" fontFamily="'IBM Plex Mono', monospace" color="teal.400">{(stats.avgConfidence * 100).toFixed(0)}%</StatNumber>
            <StatHelpText fontSize="9px" fontFamily="'IBM Plex Mono', monospace" m={0}>Isolation Forest accuracy</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Collapse>

      <Button
        size="xs"
        variant="ghost"
        rightIcon={isStatsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        onClick={onToggleStats}
        mb={4}
        fontFamily="'IBM Plex Mono', monospace" 
        letterSpacing="0.06em"
        color={textColor}
      >
        {isStatsOpen ? 'HIDE' : 'SHOW'} GRID STATISTICS
      </Button>

      <Divider mb={4} />

      {/* FILTER & SORT CONTROLS */}
      {filteredRules.length > 0 && (
        <Flex gap={3} mb={4} wrap="wrap" align="center">
          <Text fontSize="10px" color="gray.500" fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.06em">SORT BY:</Text>
          <Select
            size="xs"
            w="150px"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            fontFamily="'IBM Plex Mono', monospace" 
            borderRadius="md"
          >
            <option value="confidence">Confidence</option>
            <option value="severity">Severity</option>
            <option value="sensor">Sensor ID</option>
          </Select>
          
          {filterSeverity !== 'all' && (
            <Badge colorScheme="blue" cursor="pointer" onClick={() => setFilterSeverity('all')} fontFamily="'IBM Plex Mono', monospace" fontSize="9px">
              FILTERED: {filterSeverity.toUpperCase()} ✕
            </Badge>
          )}
        </Flex>
      )}

      {/* MAIN CONTENT LIST */}
      {isLoading ? (
        <RuleWidgetSkeleton />
      ) : filteredRules.length === 0 ? (
        <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
          <Alert
            status="success"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="xl"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Heading size="md" mt={4} mb={1} fontFamily="'Syne', sans-serif">Grid Stable</Heading>
            <AlertDescription maxWidth="sm" fontSize="sm">
              {dismissedRules.length > 0
                ? 'All AI suggestions have been reviewed and processed.'
                : 'No statistical anomalies detected. The utility grid is operating within normal ML baselines.'}
            </AlertDescription>
          </Alert>
        </MotionBox>
      ) : (
        <VStack align="stretch" spacing={3}>
          <MotionBox variants={FADE_UP} initial="hidden" animate="visible">
            <Alert status="info" variant="left-accent" borderRadius="xl">
              <AlertIcon />
              <Box fontSize="sm">
                <Text fontWeight="800" fontFamily="'Syne', sans-serif">Analysis Complete</Text>
                <Text fontSize="11px">
                  The Isolation Forest detected unusual sensor clusters across {filteredRules.length} hardware node{filteredRules.length !== 1 ? 's' : ''}.
                  Approve these suggestions to automatically generate alert rules in the database.
                </Text>
              </Box>
            </Alert>
          </MotionBox>

          {/* SCROLLABLE CONTAINER FOR LIST */}
          <Box maxH="450px" overflowY="auto" pr={2} css={scrollbarStyles}>
            <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
              <VStack align="stretch" spacing={3}>
                <AnimatePresence>
                  {filteredRules.map((rule) => (
                    <motion.div key={rule.sensor_id + rule.anomaly_type} variants={LIST_ITEM_VARIANT} exit="exit">
                      <SuggestedRuleCard
                        rule={rule}
                        onApprove={handleQuickApprove}
                        onDismiss={handleDismiss}
                        onEdit={handleEditRule}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </VStack>
            </motion.div>
          </Box>
        </VStack>
      )}

      {/* CONFIGURATION MODAL */}
      <RuleConfigModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        rule={selectedRule}
        onConfirm={handleConfirmRule}
        isCreating={isCreating}
      />
    </Card>
  );
};

export default AISuggestedRulesWidget;